import React, { FC, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { isValidEmail } from 'utils/validations';
import { useMutation } from '@apollo/client';
import { submitSupportTicketTemplate } from 'apollo/templates/zendesk';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { useLocation } from 'react-router-dom';

type SupportTicketProps = {
    /** DD-3780: a problem report the help assistant drafted; the user edits and sends it. */
    initialMessage?: string;
    /** DD-3803: "Avbryt" goes back instead of submitting. */
    onCancel: () => void;
};

const SupportTicket: FC<SupportTicketProps> = ({ initialMessage, onCancel }: SupportTicketProps) => {
    const { t: translate } = useTranslation(['common', 'help-drawer']);
    const currentUser = currentUserVar();
    const { pathname, hash } = useLocation();
    const { showBoundary } = useDenthubErrorBoundary();
    const [isTicketSubmitted, setIsTicketSubmitted] = useState(false);

    const [submitSupportTicket, { loading }] = useMutation(submitSupportTicketTemplate, {
        onCompleted: ({ submitSupportTicket }) => {
            if (submitSupportTicket === 'OK') {
                setIsTicketSubmitted(true);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (formData: SupportTicketForm) => {
        const errors: Record<string, string> = {};
        if (!formData.name) {
            errors.name = translate('help-drawer:VALIDATION:NAME_REQUIRED');
        }
        if (!formData.email) {
            errors.email = translate('help-drawer:VALIDATION:EMAIL_REQUIRED');
        }
        if (!formData.message) {
            errors.message = translate('help-drawer:VALIDATION:MESSAGE_REQUIRED');
        }
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('help-drawer:VALIDATION:INVALID_EMAIL');
        }

        return errors;
    };

    const formik = useFormik<SupportTicketForm>({
        initialValues: {
            name: currentUser.name.firstName + ' ' + currentUser.name.lastName,
            email: currentUser.email,
            message: initialMessage ?? '',
        },
        validateOnMount: true,
        validate,
        onSubmit: async (formData) => {
            // DD-3803: the backend adds the organization and, on an order page, the order number.
            // Path and tab only: a query string can carry one-time codes.
            submitSupportTicket({ variables: { ...formData, pagePath: pathname + hash } });
        },
    });

    return (
        <Box mt={1}>
            {isTicketSubmitted ? (
                <Alert severity="success">{translate('help-drawer:SUPPORT_TICKET_HAS_BEEN_SUBMITTED')}</Alert>
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <Box mb={2}>
                        <TextField
                            label={translate('common:NAME')}
                            fullWidth
                            helperText={formik.touched.name && formik.errors.name}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            {...formik.getFieldProps('name')}
                        />
                    </Box>
                    <Box mb={2}>
                        <TextField
                            label={translate('common:EMAIL')}
                            fullWidth
                            helperText={formik.touched.email && formik.errors.email}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            {...formik.getFieldProps('email')}
                        />
                    </Box>
                    <Box mb={2}>
                        <TextField
                            multiline
                            rows={10}
                            label={translate('help-drawer:DESCIBE_YOUR_ISSUE')}
                            fullWidth
                            helperText={formik.touched.message && formik.errors.message}
                            error={formik.touched.message && Boolean(formik.errors.message)}
                            {...formik.getFieldProps('message')}
                        />
                        <Typography variant="caption" color="grey">
                            {translate('help-drawer:SUPPORT_TICKET_CONTEXT_INFO')}
                        </Typography>
                    </Box>
                    <Box display="flex">
                        <Box flexGrow={1}>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                disabled={!formik.isValid}
                                loading={loading}
                            >
                                {translate('help-drawer:SEND_ISSUE')}
                            </LoadingButton>
                        </Box>
                        <Box>
                            <Button onClick={onCancel}>{translate('common:CANCEL')}</Button>
                        </Box>
                    </Box>
                </form>
            )}
        </Box>
    );
};

export default SupportTicket;

type SupportTicketForm = {
    name?: string;
    email?: string;
    message?: string;
};
